import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Button from 'components/Forms/Button/Button';
import Form from 'components/Forms/Form/Form';
import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import TextInput from 'components/Forms/TextInput/TextInput';
import { useLoginForm, useLoginFormMeta } from 'components/Pages/Login/formMeta';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { validateReturnUrl } from 'helpers/cookies/returnUrlCookieHelper';
import { handleFormErrors } from 'helpers/forms/handleFormErrors';
import { useAuth } from 'hooks/auth/useAuth';
import useTranslation from 'next-translate/useTranslation';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { usePersistStore } from 'store/usePersistStore';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type B2BLoginProps = {
    referringUrl: string | null;
    shouldOverwriteCustomerUserCart?: boolean;
};

const TEST_IDENTIFIER = 'pages-b2b-login';

const B2BLogin: FC<B2BLoginProps> = ({ referringUrl, shouldOverwriteCustomerUserCart }) => {
    const { t } = useTranslation();
    const { cartUuid } = usePersistStore((s) => s);
    const { url } = useDomainConfig();
    const { login } = useAuth();
    const [resetPasswordUrl] = getInternationalizedStaticUrls(['/b2b-reset-password'], url);
    const [formProviderMethods] = useLoginForm();
    const {
        fields: { email: emailField, password: passwordField },
        formName,
    } = useLoginFormMeta(formProviderMethods);

    const onLoginHandler: SubmitHandler<{ email: string; password: string }> = async (data) => {
        const redirectUrl = validateReturnUrl(referringUrl, url) ?? '/';

        const loginResult = await login(
            {
                email: data.email,
                password: data.password,
                previousCartUuid: cartUuid,
                deleteOldCustomerCart: false,
                shouldOverwriteCustomerUserCart,
            },
            redirectUrl,
        );

        handleFormErrors(loginResult.error, formProviderMethods, t, undefined, undefined);
    };

    return (
        <div className="mx-auto flex max-w-[1000px] flex-col lg:mt-[100px] lg:flex-row">
            <div className="flex flex-col items-center justify-center gap-[30px] bg-grayLight p-[30px] lg:w-1/2">
                <p>
                    {t(
                        'Welcome to the website of the NEOS electronic and information ordering system. You can easily place your orders for goods from Tescoma in it, and at the same time, it will provide you with all the necessary information about events, news and changes that you should not miss.',
                    )}
                </p>
                <p>
                    {t(
                        'Please send any questions, comments or information about errors to the email address neos@tescoma.cz',
                    )}
                </p>
            </div>
            <FormProvider {...formProviderMethods}>
                <Form
                    className="flex flex-col bg-grayVeryLight p-6 lg:w-1/2 lg:p-[60px_46px]"
                    onSubmit={formProviderMethods.handleSubmit(onLoginHandler)}
                >
                    <h1 className="text-[22px] lg:mb-[20px] lg:text-[28px]" data-testid="basic-heading-h1">
                        {t('Log in')}
                    </h1>
                    <Controller
                        name={emailField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine bottomGap>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id="login_form-email"
                                    isTouched={isTouched}
                                    label={emailField.label}
                                    name={emailField.name}
                                    type="text"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${emailField.name}-error`}
                                    error={error}
                                    inputType="text-input"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <Controller
                        name={passwordField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id="login_form-password"
                                    isTouched={isTouched}
                                    label={passwordField.label}
                                    name={passwordField.name}
                                    type="password"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${passwordField.name}-error`}
                                    error={error}
                                    inputType="text-input-password"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <div className="mt-4 flex flex-col items-start gap-[30px] lg:flex-row lg:items-center lg:gap-[46px]">
                        <Button data-testid={`${TEST_IDENTIFIER}-button`} type="submit" variant="primary">
                            {t('Log in')}
                        </Button>
                        <ExtendedNextLink
                            className="block w-fit break-words leading-4 text-primary underline hover:text-grayDark hover:no-underline"
                            href={resetPasswordUrl}
                        >
                            {t('Lost your password?')}
                        </ExtendedNextLink>
                    </div>
                </Form>
            </FormProvider>
        </div>
    );
};

export default B2BLogin;
